import { v4 as uuidv4 } from 'uuid'
import { SyncQueueItem, SyncStatus, SyncConflict, PatientMatchCandidate, PatientSearchCriteria, OfflinePatient, OfflineConsultation, OfflineDocument } from '../types/database.types'
import { Patient, CreatePatientDto, UpdatePatientDto } from '@/core/models/patient.model'
import { Consultation, ConsultationStatus, CreateConsultationDto, UpdateConsultationDto } from '@/core/models/consultation.model'
import patientModel from '@/core/models/patient.model'
import consultationModel from '@/core/models/consultation.model'
import documentModel, { CreateDocumentDto } from '@/core/models/document.model'
import { syncHttpClient } from '@/core/classes/HttpClient'

// Sync-aware API wrappers that use syncHttpClient
const syncAwareApi = {
    patient: {
        create: (data: CreatePatientDto, token?: string) =>
            syncHttpClient.request<Patient>('/patients', 'POST', data, token),
        update: (id: string, data: UpdatePatientDto) =>
            syncHttpClient.request<Patient>(`/patients/${id}`, 'PATCH', data),
        search: (query: any, options?: any) =>
            syncHttpClient.request<[Patient[], number]>('/patients/query', 'POST', { query, options })
    },
    consultation: {
        create: (data: CreateConsultationDto) =>
            syncHttpClient.request<Consultation>('/consultations', 'POST', data),
        update: (id: string, data: UpdateConsultationDto) =>
            syncHttpClient.request<Consultation>(`/consultations/${id}`, 'PATCH', data)
    },
    document: {
        generatePresignedUrl: (params: any) =>
            syncHttpClient.request<{ url: string; fileName: string }>('/files/generate-presigned-url', 'POST', params),
        uploadToS3: (url: string, file: File) => {
            // For S3 upload, we need to use axios directly since it's a different endpoint
            return syncHttpClient.axios.put(url, file, {
                headers: { 'Content-Type': file.type, 'Authorization': null }
            })
        },
        create: (data: CreateDocumentDto, objectType: string, objectId: string) =>
            syncHttpClient.request<any>(`/files/${objectType}/${objectId}`, 'POST', { file: { ...data, objectType } })
    }
}
import { SyncProgress } from '../types/database.types'
import { offlineDatabase } from '../store/database'
import { documentOfflineStore } from '../store/document.store'
import { consultationOfflineStore } from '../store/consultation.store'
import { patientOfflineStore } from '../store/patient.store'
import { conflictsOfflineStore } from '../store/conflicts.store'

export class SyncService {
    private isSyncing: boolean = false
    private syncInterval: number = 5 * 60 * 1000 // 5 minutes
    private maxRetries: number = 3
    private syncTimer?: NodeJS.Timeout
    private syncListeners: ((status: SyncStatus) => void)[] = []
    private conflictListeners: ((conflict: SyncConflict) => void)[] = []
    private progressListeners: ((progress: SyncProgress) => void)[] = []
    private authErrorListeners: ((retryCallback: () => Promise<void>) => void)[] = []

    constructor() {
        this.startPeriodicSync()

        // Automatically start syncing when online
        window.addEventListener('online', () => {
            this.sync()
        })

        // Attempt a deferred sync shortly after startup to allow DB init
        setTimeout(() => {
            try {
                offlineDatabase.getDatabase()
                this.sync()
            } catch (_) {
                // DB not ready yet; will be picked up by periodic sync or manual trigger
            }
        }, 1500)
    }

    // Event listeners for UI updates
    onSyncStatusChange(listener: (status: SyncStatus) => void): () => void {
        this.syncListeners.push(listener)
        return () => {
            const index = this.syncListeners.indexOf(listener)
            if (index > -1) {
                this.syncListeners.splice(index, 1)
            }
        }
    }

    onConflictDetected(listener: (conflict: SyncConflict) => void): () => void {
        this.conflictListeners.push(listener)
        return () => {
            const index = this.conflictListeners.indexOf(listener)
            if (index > -1) {
                this.conflictListeners.splice(index, 1)
            }
        }
    }

    private notifySyncStatus(status: SyncStatus): void {
        this.syncListeners.forEach(listener => listener(status))
    }

    private notifyConflict(conflict: SyncConflict): void {
        this.conflictListeners.forEach(listener => listener(conflict))
    }

    private startPeriodicSync(): void {
        this.syncTimer = setInterval(() => {
            this.sync()
        }, this.syncInterval)
    }

    async stopPeriodicSync(): Promise<void> {
        if (this.syncTimer) {
            clearInterval(this.syncTimer)
        }
    }

    async getSyncStatus(): Promise<SyncStatus> {
        try {
            const db = offlineDatabase.getDatabase()
            const transaction = db.transaction('syncQueue', 'readonly')
            const store = transaction.objectStore('syncQueue')

            return new Promise((resolve) => {
                const request = store.getAll()
                request.onsuccess = () => {
                    const items: SyncQueueItem[] = request.result
                    const pendingItems = items.filter(item => item.status === 'pending').length
                    const failedItems = items.filter(item => item.status === 'failed').length
                    const conflictItems = items.filter(item => item.status === 'conflict').length
                    const totalItems = items.length
                    const completedItems = items.filter(item => item.status === 'completed').length

                    resolve({
                        isActive: this.isSyncing,
                        lastSync: localStorage.getItem('last_sync_timestamp') ? new Date(localStorage.getItem('last_sync_timestamp')!).getTime() : undefined,
                        pendingCount: pendingItems,
                        failedCount: failedItems,
                        completedCount: completedItems,
                        totalCount: totalItems,
                        hasConflicts: conflictItems > 0
                    })
                }
            })
        } catch (error) {
            console.warn('Database not ready for sync status, returning default status:', error)
            // Return default status when database is not initialized
            return {
                isActive: false,
                lastSync: undefined,
                pendingCount: 0,
                failedCount: 0,
                completedCount: 0,
                totalCount: 0,
                hasConflicts: false
            }
        }
    }

    async sync(): Promise<void> {
        if (this.isSyncing) {
            return
        }

        try {
            // Check if database is ready before starting sync
            offlineDatabase.getDatabase()
        } catch (error) {
            console.warn('Database not ready for sync, skipping sync operation:', error)
            return
        }

        this.isSyncing = true
        const status = await this.getSyncStatus()
        this.notifySyncStatus({ ...status, isActive: true })

        try {
            this.notifyProgress({
                stage: 'preparing',
                progress: 0,
                message: 'Preparing sync...'
            })

            // Recover any items left in processing state from a previous/failed run
            await this.recoverProcessingItems()

            const db = offlineDatabase.getDatabase()
            const transaction = db.transaction('syncQueue', 'readwrite')
            const store = transaction.objectStore('syncQueue')
            const index = store.index('status')
            const request = index.getAll('pending')

            request.onsuccess = async () => {
                const queueItems: SyncQueueItem[] = request.result

                if (queueItems.length === 0) {
                    // Before bailing out, attempt a scan to discover any missed unsynced items
                    try {
                        await this.scanAndQueueUnsyncedItems()
                        const refreshed = await new Promise<SyncQueueItem[]>((resolve) => {
                            const db2 = offlineDatabase.getDatabase()
                            const tx2 = db2.transaction('syncQueue', 'readonly')
                            const store2 = tx2.objectStore('syncQueue')
                            const index2 = store2.index('status')
                            const req2 = index2.getAll('pending')
                            req2.onsuccess = () => resolve(req2.result)
                            req2.onerror = () => resolve([])
                        })
                        if (refreshed.length > 0) {
                            // Restart sync loop now that we have items
                            this.isSyncing = false
                            return this.sync()
                        }
                    } catch (_) { /* ignore */ }
                    this.notifyProgress({
                        stage: 'completing',
                        progress: 100,
                        message: 'No items to sync'
                    })
                    return
                }

                // Sort by priority and dependencies
                const sortedItems = this.sortItemsByDependencies(queueItems)
                const totalItems = sortedItems.length

                for (let i = 0; i < sortedItems.length; i++) {
                    const item = sortedItems[i]
                    const progress = Math.round((i / totalItems) * 100)

                    this.notifyProgress({
                        currentItem: {
                            entityType: item.entityType,
                            entityId: item.entityId,
                            action: item.action || item.operation,
                            description: this.getItemDescription(item)
                        },
                        stage: 'syncing',
                        progress,
                        message: `Syncing ${item.entityType} (${i + 1}/${totalItems})`
                    })

                    try {
                        await this.processQueueItem(item)
                        const updatedStatus = await this.getSyncStatus()
                        this.notifySyncStatus(updatedStatus)
                    } catch (error) {
                        console.error(`Failed to process queue item:`, error)
                        // Error handling is now done inside processQueueItem
                    }
                }

                this.notifyProgress({
                    stage: 'completing',
                    progress: 100,
                    message: 'Sync completed'
                })

                localStorage.setItem('last_sync_timestamp', new Date().toISOString())
            }

            request.onerror = () => {
                console.error('Failed to get pending sync items')
                this.notifyProgress({
                    stage: 'completing',
                    progress: 100,
                    message: 'Failed to get sync items'
                })
            }
        } finally {
            this.isSyncing = false
            const finalStatus = await this.getSyncStatus()
            this.notifySyncStatus({ ...finalStatus, isActive: false })
        }
    }

    private sortItemsByDependencies(items: SyncQueueItem[]): SyncQueueItem[] {
        console.log(`Sorting ${items.length} sync items by dependencies`)

        const sorted: SyncQueueItem[] = []
        const remaining = [...items]
        const processed = new Set<string>()

        let iterations = 0
        while (remaining.length > 0 && iterations < 100) { // Prevent infinite loops
            iterations++
            console.log(`Dependency sort iteration ${iterations}, remaining items: ${remaining.length}`)

            const canProcess = remaining.filter(item =>
                item.dependencies.every(dep => processed.has(dep))
            )

            console.log(`Items that can be processed: ${canProcess.length}`)
            canProcess.forEach(item => {
                console.log(`- ${item.entityType} ${item.entityId} (priority: ${item.priority}, deps: [${item.dependencies.join(', ')}])`)
            })

            if (canProcess.length === 0) {
                // Break circular dependencies by processing highest priority items
                const highestPriority = Math.max(...remaining.map(item => item.priority))
                const item = remaining.find(item => item.priority === highestPriority)!
                console.log(`Breaking dependency cycle by processing highest priority item: ${item.entityType} ${item.entityId}`)
                sorted.push(item)
                processed.add(item.id)
                remaining.splice(remaining.indexOf(item), 1)
            } else {
                // Sort by priority within processable items
                canProcess.sort((a, b) => b.priority - a.priority)
                for (const item of canProcess) {
                    sorted.push(item)
                    processed.add(item.id)
                    remaining.splice(remaining.indexOf(item), 1)
                }
            }
        }

        console.log(`Dependency sorting complete. Final order:`)
        sorted.forEach((item, index) => {
            console.log(`${index + 1}. ${item.entityType} ${item.entityId} (priority: ${item.priority})`)
        })

        return sorted
    }

    private async processQueueItem(item: SyncQueueItem): Promise<void> {
        // Create initial transaction for status update
        let db = offlineDatabase.getDatabase()
        let transaction = db.transaction('syncQueue', 'readwrite')
        let store = transaction.objectStore('syncQueue')

        // Update status to processing
        item.status = 'processing'
        item.updatedAt = new Date().toISOString()
        await this.updateQueueItem(store, item)

        try {
            // Additional check for documents: ensure their parent objects are synced
            if (item.entityType === 'document') {
                console.log(`Checking parent sync status for document: ${item.entityId}`)
                const isParentReady = await this.checkDocumentParentSyncStatus(item.entityId)
                console.log(`Parent ready status for document ${item.entityId}: ${isParentReady}`)

                if (!isParentReady) {
                    console.log(`Parent not ready for document ${item.entityId}, deferring sync`)
                    // Parent not ready, mark as pending and retry later
                    db = offlineDatabase.getDatabase()
                    transaction = db.transaction('syncQueue', 'readwrite')
                    store = transaction.objectStore('syncQueue')

                    item.status = 'pending'
                    item.updatedAt = new Date().toISOString()
                    await this.updateQueueItem(store, item)
                    return // Skip processing for now, will be retried later
                }

                console.log(`Parent is ready for document ${item.entityId}, proceeding with sync`)
            }

            switch (item.entityType) {
                case 'patient':
                    await this.syncPatient(item)
                    break
                case 'consultation':
                    await this.syncConsultation(item)
                    break
                case 'document':
                    await this.syncDocument(item)
                    break
                default:
                    throw new Error(`Unsupported entity type: ${item.entityType}`)
            }

            // Create new transaction for completion update to avoid inactive transaction error
            db = offlineDatabase.getDatabase()
            transaction = db.transaction('syncQueue', 'readwrite')
            store = transaction.objectStore('syncQueue')

            // Mark as completed
            item.status = 'completed'
            item.updatedAt = new Date().toISOString()
            await this.updateQueueItem(store, item)
        } catch (error) {
            // Create new transaction for error update to avoid inactive transaction error
            db = offlineDatabase.getDatabase()
            transaction = db.transaction('syncQueue', 'readwrite')
            store = transaction.objectStore('syncQueue')

            // Update error status
            item.retryCount++
            item.lastError = error instanceof Error ? error.message : String(error)
            item.updatedAt = new Date().toISOString()

            if (item.retryCount >= item.maxRetries) {
                item.status = 'failed'
            } else {
                item.status = 'pending'
            }

            await this.updateQueueItem(store, item)
            throw error
        } finally {
            // Ensure we never leave the item stuck in processing
            try {
                const db2 = offlineDatabase.getDatabase()
                const tx2 = db2.transaction('syncQueue', 'readwrite')
                const store2 = tx2.objectStore('syncQueue')
                const current = await new Promise<SyncQueueItem | undefined>((resolve) => {
                    const getReq = store2.get(item.id)
                    getReq.onsuccess = () => resolve(getReq.result)
                    getReq.onerror = () => resolve(undefined)
                })
                if (current && current.status === 'processing') {
                    current.status = 'pending'
                    current.updatedAt = new Date().toISOString()
                    current.lastError = current.lastError || 'Recovered from processing state (finally)'
                    await this.updateQueueItem(store2, current)
                }
            } catch (_) { /* ignore */ }
        }
    }

    // Check if a document's parent object is ready for document sync
    private async checkDocumentParentSyncStatus(documentId: string): Promise<boolean> {
        try {
            console.log(`Checking parent sync status for document ID: ${documentId}`)

            // Get the document
            let document = await documentOfflineStore.get(documentId)

            // If not found by regular ID, try by offlineId
            if (!document) {
                const allDocuments = await documentOfflineStore.getAll()
                document = allDocuments.find(doc =>
                    doc._offlineId === documentId ||
                    doc._id === documentId
                )

            }

            if (!document) {
                console.log(`Document not found for ID: ${documentId}`)
                return false // Document not found
            }

            console.log(`Document found: ${document.filename}, objectType: ${document.objectType}, objectId: ${document.objectId}`)

            // Check the parent object's sync status
            if (document.objectType === 'consultation') {
                const consultation = await consultationOfflineStore.get(document.objectId)
                console.log(`Consultation found: ${!!consultation}`)
                if (consultation) {
                    console.log(`Consultation _cloudId: ${consultation._cloudId}, _syncStatus: ${consultation._syncStatus}`)
                }
                return !!(consultation && consultation._cloudId !== undefined)
            } else if (document.objectType === 'patient') {
                const patient = await patientOfflineStore.get(document.objectId)
                console.log(`Patient found: ${!!patient}`)
                if (patient) {
                    console.log(`Patient _cloudId: ${patient._cloudId}, _syncStatus: ${patient._syncStatus}`)
                }
                return !!(patient && patient._cloudId !== undefined)
            }

            console.log(`Other object type ${document.objectType}, assuming ready`)
            return true // For other object types, assume ready
        } catch (error) {
            console.error('Error checking document parent sync status:', error)
            return false
        }
    }

    private async syncPatient(item: SyncQueueItem): Promise<void> {
        const patient = await patientOfflineStore.get(item.entityId) as OfflinePatient
        if (!patient) {
            throw new Error(`Patient not found: ${item.entityId}`)
        }

        try {
            let syncedPatient: Patient

            if (item.operation === 'create') {

                // Create patient via API
                syncedPatient = await this.createPatientAPI(patient)
            } else {
                // Update patient via API
                syncedPatient = await this.updatePatientAPI(patient)
            }

            // Update local patient with cloud ID and sync status
            await patientOfflineStore.markAsSynced(patient, syncedPatient._id)

            // Update all consultations that reference this patient with the new cloud ID
            await this.updateConsultationsWithPatientCloudId(patient._id, syncedPatient._id)
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error)
            patient._syncErrors = [errorMessage]
            patient._lastSyncAttempt = new Date().toISOString()
            await patientOfflineStore.update(patient)
            throw error
        }
    }

    private async syncConsultation(item: SyncQueueItem): Promise<void> {
        const consultation = await consultationOfflineStore.get(item.entityId) as OfflineConsultation
        if (!consultation) {
            throw new Error(`Consultation not found: ${item.entityId}`)
        }

        // Get the patient ID to use for the consultation
        let patientCloudId: string
        if (typeof consultation.patient === 'string') {
            patientCloudId = consultation.patient
        } else {
            // Get the patient and ensure it's synced
            const patient = await patientOfflineStore.get(consultation.patient._id) as OfflinePatient
            if (!patient) {
                throw new Error(`Patient not found: ${consultation.patient._id}`)
            }

            if (patient._syncStatus !== 'synced' || !patient._cloudId) {
                throw new Error('Patient must be synced before consultation')
            }

            patientCloudId = patient._cloudId // Use the cloud ID, not the offline ID
        }

        try {
            let syncedConsultation: Consultation

            if (item.operation === 'create') {
                // Create a temporary consultation object with the correct patient ID for API call
                const consultationForAPI = {
                    ...consultation,
                    patient: patientCloudId as any // Temporarily override type for API call
                }
                syncedConsultation = await this.createConsultationAPI(consultationForAPI)

                // After creation, update with all the local fields that weren't in the creation DTO
                // The backend initializes some fields during creation, but we need to sync our local data
                try {
                    // Create a consultation with the cloud ID for update
                    const consultationWithCloudId = { ...consultation, _cloudId: syncedConsultation._id }
                    const updatedConsultation = await this.updateConsultationAPI(consultationWithCloudId)
                    syncedConsultation = updatedConsultation
                } catch (updateError) {
                    console.warn('Failed to update consultation with local data after creation:', updateError)
                    // Continue with the created consultation even if update fails
                }
            } else {
                syncedConsultation = await this.updateConsultationAPI(consultation)
            }

            // Update local consultation with cloud ID and sync status
            await consultationOfflineStore.markAsSynced(consultation, syncedConsultation._id)
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error)
            consultation._syncErrors = [errorMessage]
            consultation._lastSyncAttempt = new Date().toISOString()
            await consultationOfflineStore.update(consultation)
            throw error
        }
    }

    private base64ToBlob(base64Data: string, contentType: string): Blob {
        const byteCharacters = atob(base64Data.split(',')[1] || base64Data)
        const byteNumbers = new Array(byteCharacters.length)
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i)
        }
        const byteArray = new Uint8Array(byteNumbers)
        return new Blob([byteArray], { type: contentType })
    }

    private async syncDocument(item: SyncQueueItem): Promise<void> {
        // Try to get document by ID or offlineId
        let document = await documentOfflineStore.get(item.entityId)

        // If not found by regular ID, try by offlineId
        if (!document) {
            const allDocuments = await documentOfflineStore.getAll()
            document = allDocuments.find(doc =>
                doc._offlineId === item.entityId ||
                doc._id === item.entityId
            )
        }

        if (!document) {
            console.warn(`Document not found in store: ${item.entityId}. Marking sync item as completed.`)
            // Document was likely deleted locally, mark sync item as completed to clean it up
            return
        }

        if (!document._fileData) {
            throw new Error(`Document ${document.filename} has no file data to upload`)
        }

        console.log(`Starting sync for document: ${document.filename} (ID: ${item.entityId})`)

        try {
            // Step 1: Resolve the actual object ID (might be offline ID, need cloud ID)
            this.notifyProgress({
                stage: 'syncing',
                currentStep: 'Resolving consultation...',
                progress: 10,
                message: `Resolving consultation for ${document.filename}`
            })

            let actualObjectId = document.objectId

            if (document.objectType === 'consultation') {
                const consultation = await consultationOfflineStore.get(document.objectId)
                if (consultation && consultation._cloudId) {
                    actualObjectId = consultation._cloudId
                } else if (consultation && !consultation._cloudId) {
                    throw new Error('Consultation must be synced before its documents')
                } else if (!consultation) {
                    throw new Error(`Consultation not found: ${document.objectId}`)
                }
            } else if (document.objectType === 'patient') {
                const patient = await patientOfflineStore.get(document.objectId)
                if (patient && patient._cloudId) {
                    actualObjectId = patient._cloudId
                } else if (patient && !patient._cloudId) {
                    throw new Error('Patient must be synced before its documents')
                } else if (!patient) {
                    throw new Error(`Patient not found: ${document.objectId}`)
                }
            }

            console.log(`Resolved object ID: ${actualObjectId} for document ${document.filename}`)

            // Step 2: Generate presigned URL
            this.notifyProgress({
                stage: 'syncing',
                currentStep: 'Getting upload URL...',
                progress: 20,
                message: `Getting upload URL for ${document.filename}`
            })

            const presignedResponse = await this.executeWithAuthHandling(
                () => syncAwareApi.document.generatePresignedUrl({
                    fileName: document!.filename,
                    fileType: document!._fileType || 'application/octet-stream',
                    objectType: document!.objectType as any,
                    objectId: actualObjectId
                }),
                `presigned URL generation for ${document!.filename}`
            )
            console.log(`Generated presigned URL for document ${document.filename}`)

            // Step 3: Upload file to S3
            this.notifyProgress({
                stage: 'uploading',
                currentStep: 'Uploading to S3...',
                progress: 30,
                message: `Uploading ${document.filename} to S3...`
            })

            const fileBlob = this.base64ToBlob(document!._fileData, document!._fileType || 'application/octet-stream')
            const file = new File([fileBlob], presignedResponse.fileName, { type: document!._fileType || 'application/octet-stream' })

            await this.executeWithAuthHandling(
                () => syncAwareApi.document.uploadToS3(presignedResponse.url, file),
                `S3 upload for ${document.filename}`
            )

            console.log(`Uploaded file to S3 for document ${document.filename}`)

            // Step 4: Add to backend
            this.notifyProgress({
                stage: 'syncing',
                currentStep: 'Adding to backend...',
                progress: 80,
                message: `Registering ${document.filename} in backend`
            })

            const documentDto: CreateDocumentDto = {
                filename: presignedResponse.fileName,
                key: `${document!.objectType}/${actualObjectId}/${presignedResponse.fileName}`,
                objectType: document!.objectType as any,
                ...(document!.isRecording && { isRecording: document!.isRecording })
            }

            let syncedDocument: any

            if (document!.biologicalId) {
                console.log(`Syncing biological document ${document!.filename}`)
                const updatedConsultation = await documentModel.addBiologicalDocument(
                    documentDto,
                    document!.objectType as any,
                    actualObjectId,
                    document!.biologicalId
                )
                console.log(`Successfully added biological document to backend`)

                // Update the local consultation with the new document references
                await this.updateLocalConsultationAfterDocumentSync(actualObjectId, updatedConsultation)

                // For biological documents, construct the document info
                syncedDocument = {
                    key: documentDto.key,
                    filename: document!.filename,
                    url: presignedResponse.url.split('?')[0] // Remove query params to get base URL
                }
            } else {
                console.log(`Syncing regular document ${document!.filename}`)
                syncedDocument = await this.executeWithAuthHandling(
                    () => syncAwareApi.document.create(documentDto, document!.objectType, actualObjectId),
                    `document registration for ${document!.filename}`
                )
                console.log(`Successfully added regular document to backend`)
            }

            console.log(`About to mark document as synced: ${document!.filename}, documentId: ${syncedDocument.key}, documentUrl: ${syncedDocument.url}`)

            // Step 5: Mark as synced
            this.notifyProgress({
                stage: 'completing',
                currentStep: 'Finalizing...',
                progress: 95,
                message: `Finalizing sync for ${document!.filename}`
            })

            await documentOfflineStore.markAsSynced(document!, syncedDocument.key, syncedDocument.url)
            console.log(`Successfully synced document: ${document!.filename}`)

        } catch (error) {
            console.error(`Error syncing document:`, error)
            const errorMessage = error instanceof Error ? error.message : String(error)
            await documentOfflineStore.markAsFailed(document!, errorMessage)
            console.log(`Updated document with sync error: ${document!.filename}`)
            throw error
        }
    }

    private async findPotentialDuplicatePatients(patient: OfflinePatient): Promise<PatientMatchCandidate[]> {
        try {
            // Search for patients with similar names, birth dates, or phone numbers
            const searchQueries = []

            if (patient.userInfo?.name && patient.userInfo?.surname) {
                searchQueries.push({
                    name: patient.userInfo.name,
                    surname: patient.userInfo.surname
                    // Remove patientSource since ALL doesn't exist
                })
            }

            if (patient.secu) {
                searchQueries.push({
                    secu: parseInt(patient.secu)
                    // Remove patientSource since ALL doesn't exist
                })
            }

            const duplicateResults: PatientMatchCandidate[] = []

            for (const query of searchQueries) {
                const [patients] = await this.executeWithAuthHandling(
                    () => syncAwareApi.patient.search(query, { limit: 10 }),
                    'patient search during sync'
                )

                for (const foundPatient of patients) {
                    const score = this.calculatePatientMatchScore(patient, foundPatient)
                    if (score > 0.7) { // 70% match threshold
                        const matchReasons = this.getMatchReasons(patient, foundPatient)
                        duplicateResults.push({
                            patient: foundPatient,
                            score,
                            matchReasons
                        })
                    }
                }
            }

            // Remove duplicates and sort by score
            const uniqueDuplicates = duplicateResults.reduce((acc, current) => {
                const existing = acc.find(item => item.patient._id === current.patient._id)
                if (!existing || current.score > existing.score) {
                    acc = acc.filter(item => item.patient._id !== current.patient._id)
                    acc.push(current)
                }
                return acc
            }, [] as PatientMatchCandidate[])

            return uniqueDuplicates.sort((a, b) => b.score - a.score)
        } catch (error) {
            console.error('Error finding duplicate patients:', error)
            return []
        }
    }

    private calculatePatientMatchScore(offline: OfflinePatient, online: Patient): number {
        let score = 0
        let totalChecks = 0

        // Name similarity
        if (offline.userInfo?.name && online.userInfo?.name) {
            totalChecks++
            if ((offline.userInfo.name ?? '').toLowerCase() === online.userInfo.name.toLowerCase()) {
                score += 0.3
            }
        }

        // Surname similarity  
        if (offline.userInfo?.surname && online.userInfo?.surname) {
            totalChecks++
            if ((offline.userInfo.surname ?? '').toLowerCase() === online.userInfo.surname.toLowerCase()) {
                score += 0.3
            }
        }

        // Birth date match
        if (offline.userInfo?.birthday && online.userInfo?.birthday) {
            totalChecks++
            if (offline.userInfo.birthday === online.userInfo.birthday) {
                score += 0.25
            }
        }

        // Social security number match
        if (offline.secu && online.secu) {
            totalChecks++
            if (offline.secu === online.secu) {
                score += 0.4 // High weight for social security match
            }
        }

        // Phone number match
        if (offline.contactInfo?.phone && online.contactInfo?.phone) {
            totalChecks++
            if (offline.contactInfo.phone === online.contactInfo.phone) {
                score += 0.15
            }
        }

        return totalChecks > 0 ? score : 0
    }

    private getMatchReasons(offline: OfflinePatient, online: Patient): string[] {
        const reasons: string[] = []

        if (offline.userInfo?.name?.toLowerCase() === online.userInfo?.name?.toLowerCase() &&
            offline.userInfo?.surname?.toLowerCase() === online.userInfo?.surname?.toLowerCase()) {
            reasons.push('Nom et prénom identiques')
        }

        if (offline.userInfo?.birthday === online.userInfo?.birthday) {
            reasons.push('Date de naissance identique')
        }

        if (offline.secu === online.secu) {
            reasons.push('Numéro de sécurité sociale identique')
        }

        if (offline.contactInfo?.phone === online.contactInfo?.phone) {
            reasons.push('Numéro de téléphone identique')
        }

        return reasons
    }

    private async createPatientAPI(patient: OfflinePatient): Promise<Patient> {
        // Convert offline patient to CreatePatientDto - only include valid fields
        const createPatientDto: CreatePatientDto = {
            userInfo: patient.userInfo,
            contactInfo: patient.contactInfo,
            agreement: patient.agreement || '',
            // Only include optional fields if they exist and are valid
            ...(patient.allergy && { allergy: patient.allergy }),
            ...(patient.vaccins && { vaccins: patient.vaccins }),
            ...(patient.background && { background: patient.background }),
            ...(patient.treatment && { treatment: patient.treatment }),
            ...(patient.notes && { notes: patient.notes }),
            ...(patient.codeGestion && { codeGestion: patient.codeGestion }),
            ...(patient.codeRegime && { codeRegime: patient.codeRegime }),
            ...(patient.caisse && { caisse: patient.caisse }),
            ...(patient.imc && { imc: patient.imc }),
            ...(patient.height && { height: patient.height }),
            ...(patient.weight && { weight: patient.weight }),
            ...(patient.attendingPhysician && { attendingPhysician: patient.attendingPhysician }),
            ...(patient.pluginId && { pluginId: patient.pluginId }),
            ...(patient.netsoinsId && { netsoinsId: patient.netsoinsId }),
            ...(patient.secu && { secu: patient.secu }),
            // Only include email if it's valid
            ...(patient.email && patient.email.includes('@') && { email: patient.email }),
            active: patient.active !== undefined ? patient.active : true
        }

        return await this.executeWithAuthHandling(
            () => syncAwareApi.patient.create(createPatientDto),
            'patient creation during sync'
        )
    }

    private async updatePatientAPI(patient: OfflinePatient): Promise<Patient> {
        if (!patient._cloudId) {
            throw new Error('Cannot update patient without cloud ID')
        }

        // Convert offline patient to UpdatePatientDto
        const updatePatientDto: UpdatePatientDto = {
            userInfo: patient.userInfo,
            contactInfo: patient.contactInfo,
            agreement: patient.agreement,
            // Only include optional fields if they exist and are valid
            ...(patient.allergy && { allergy: patient.allergy }),
            ...(patient.vaccins && { vaccins: patient.vaccins }),
            ...(patient.background && { background: patient.background }),
            ...(patient.treatment && { treatment: patient.treatment }),
            ...(patient.notes && { notes: patient.notes }),
            ...(patient.codeGestion && { codeGestion: patient.codeGestion }),
            ...(patient.codeRegime && { codeRegime: patient.codeRegime }),
            ...(patient.caisse && { caisse: patient.caisse }),
            ...(patient.imc && { imc: patient.imc }),
            ...(patient.height && { height: patient.height }),
            ...(patient.weight && { weight: patient.weight }),
            ...(patient.attendingPhysician && { attendingPhysician: patient.attendingPhysician }),
            ...(patient.secu && { secu: patient.secu }),
            active: patient.active
        }

        return await this.executeWithAuthHandling(
            () => syncAwareApi.patient.update(patient._cloudId!, updatePatientDto),
            'patient update during sync'
        )
    }

    private async createConsultationAPI(consultation: OfflineConsultation): Promise<Consultation> {
        // Get the patient ID - should be a string (cloud ID) at this point
        const patientId = typeof consultation.patient === 'string'
            ? consultation.patient
            : consultation.patient._id

        // Convert offline consultation to CreateConsultationDto
        const createConsultationDto: CreateConsultationDto = {
            patient: patientId,
            startDate: consultation.startDate,
            type: consultation.type,
            status: consultation.status,
            // Only include optional fields if they exist
            ...(consultation.commentaire && { commentaire: consultation.commentaire }),
            ...(consultation.reason && { reason: consultation.reason }),
            ...(consultation.price && { price: consultation.price }),
            ...(consultation.paymentType && { paymentType: consultation.paymentType }),
            ...(consultation.notification && { notification: consultation.notification }),
            ...(consultation.waitingRoom && { waitingRoom: consultation.waitingRoom }),
            ...(consultation.transcription && { transcription: consultation.transcription }),
        }

        return await this.executeWithAuthHandling(
            () => syncAwareApi.consultation.create(createConsultationDto),
            'consultation creation during sync'
        )
    }

    private async updateConsultationAPI(consultation: OfflineConsultation): Promise<Consultation> {
        if (!consultation._cloudId) {
            throw new Error('Cannot update consultation without cloud ID')
        }

        // Convert offline consultation to UpdateConsultationDto
        const updateConsultationDto: UpdateConsultationDto = {
            // Preserve the current consultation status instead of forcing Completed
            status: consultation.status,
            // Only include optional fields if they exist
            ...(consultation.commentaire && { commentaire: consultation.commentaire }),
            ...(consultation.reason && { reason: consultation.reason }),
            ...(consultation.price && { price: consultation.price }),
            ...(consultation.paymentType && { paymentType: consultation.paymentType }),
            ...(consultation.notification && { notification: consultation.notification }),
            ...(consultation.waitingRoom && { waitingRoom: consultation.waitingRoom }),
            ...(consultation.transcription && { transcription: consultation.transcription }),
            ...(consultation.endDate && { endDate: consultation.endDate }),
            // Include mesures if they exist and have values
            ...(consultation.mesures && consultation.mesures.length > 0 && { mesures: consultation.mesures }),
            // Include biologicals if they exist
            ...(consultation.biologicals && consultation.biologicals.length > 0 && { biologicals: consultation.biologicals })
        }

        return await this.executeWithAuthHandling(
            () => syncAwareApi.consultation.update(consultation._cloudId!, updateConsultationDto),
            'consultation update during sync'
        )
    }

    private async createSyncConflict(conflictData: Omit<SyncConflict, '_id' | '_syncStatus' | '_lastModified' | '_createdAt' | '_offlineId' | 'createdAt'>): Promise<void> {
        // Store conflict in IndexedDB using conflicts store
        const conflict = await conflictsOfflineStore.addConflict(conflictData)

        // Update sync item status to conflict
        const db = offlineDatabase.getDatabase()
        const transaction = db.transaction('syncQueue', 'readwrite')
        const store = transaction.objectStore('syncQueue')

        const queueItems = await this.getAllSyncQueueItems()
        const item = queueItems.find(item => item.entityId === conflict.entityId)
        if (item) {
            item.status = 'conflict'
            item.lastError = conflict.message
            item.updatedAt = new Date().toISOString()
            await this.updateQueueItem(store, item)
        }

        this.notifyConflict(conflict)
    }

    async getAllConflicts(): Promise<SyncConflict[]> {
        return await conflictsOfflineStore.getAll()
    }

    async resolveConflict(conflictId: string, resolution: 'use_local' | 'use_remote' | 'merge'): Promise<void> {
        const conflicts = await conflictsOfflineStore.getAll()
        const conflict = conflicts.find(c => c._id === conflictId)

        if (!conflict) {
            throw new Error(`Conflict with ID ${conflictId} not found`)
        }

        try {
            // Handle the resolution based on type
            if (resolution === 'use_local') {
                // Continue with local data - just remove conflict and retry sync
                await this.retryEntitySync(conflict.entityId, conflict.entityType)
            } else if (resolution === 'use_remote') {
                // Replace local data with remote data
                await this.replaceWithRemoteData(conflict)
            } else if (resolution === 'merge') {
                // Implement merge logic for duplicates
                await this.mergeConflictData(conflict)
            }

            // Remove the conflict after resolution
            await conflictsOfflineStore.resolveConflict(conflictId)
        } catch (error) {
            console.error('Failed to resolve conflict:', error)
            throw error
        }
    }

    private async retryEntitySync(entityId: string, entityType: string): Promise<void> {
        // Add the entity back to sync queue for retry
        await this.addToSyncQueue({
            entityType: entityType as any,
            entityId,
            operation: 'create',
            priority: 1,
            dependencies: [],
            maxRetries: 3,
            payload: {},
            data: {},
            timestamp: Date.now()
        })
    }

    private async replaceWithRemoteData(conflict: SyncConflict): Promise<void> {
        // Implementation depends on conflict type and entity
        // This is a placeholder for now
        console.log('Replace with remote data not yet implemented', conflict)
    }

    private async mergeConflictData(conflict: SyncConflict): Promise<void> {
        // Implementation depends on conflict type and entity
        // This is a placeholder for now  
        console.log('Merge conflict data not yet implemented', conflict)
    }

    private async getAllSyncQueueItems(): Promise<SyncQueueItem[]> {
        try {
            return new Promise((resolve, reject) => {
                const db = offlineDatabase.getDatabase()
                const transaction = db.transaction('syncQueue', 'readonly')
                const store = transaction.objectStore('syncQueue')
                const request = store.getAll()

                request.onsuccess = () => {
                    resolve(request.result)
                }

                request.onerror = () => {
                    reject(new Error('Failed to get sync queue items'))
                }
            })
        } catch (error) {
            console.warn('Database not ready for queue items, returning empty array:', error)
            return []
        }
    }

    private async handleSyncError(item: SyncQueueItem, error: any): Promise<void> {
        const db = offlineDatabase.getDatabase()
        const transaction = db.transaction('syncQueue', 'readwrite')
        const store = transaction.objectStore('syncQueue')

        item.retryCount++
        item.lastError = error.message
        item.updatedAt = new Date().toISOString()

        if (item.retryCount >= item.maxRetries) {
            item.status = 'failed'
        } else {
            item.status = 'pending'
        }

        await this.updateQueueItem(store, item)
    }

    private async updateQueueItem(store: IDBObjectStore, item: SyncQueueItem): Promise<void> {
        return new Promise((resolve, reject) => {
            const request = store.put(item)

            request.onsuccess = () => {
                resolve()
            }

            request.onerror = () => {
                reject(new Error('Failed to update queue item'))
            }
        })
    }

    // Public methods for manual sync control
    async triggerManualSync(): Promise<void> {
        return this.sync()
    }

    async addToSyncQueue(item: Omit<SyncQueueItem, 'id' | 'createdAt' | 'updatedAt' | 'status' | 'retryCount'>): Promise<void> {
        const db = offlineDatabase.getDatabase()
        const transaction = db.transaction('syncQueue', 'readwrite')
        const store = transaction.objectStore('syncQueue')

        const queueItem: SyncQueueItem = {
            ...item,
            id: uuidv4(),
            status: 'pending',
            retryCount: 0,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        }

        return new Promise((resolve, reject) => {
            const request = store.add(queueItem)

            request.onsuccess = () => {
                resolve()
            }

            request.onerror = () => {
                reject(new Error('Failed to add item to sync queue'))
            }
        })
    }

    async clearCompletedItems(): Promise<void> {
        const db = offlineDatabase.getDatabase()
        const transaction = db.transaction('syncQueue', 'readwrite')
        const store = transaction.objectStore('syncQueue')
        const index = store.index('status')
        const request = index.getAll('completed')

        await new Promise<void>((resolve, reject) => {
            request.onsuccess = async () => {
                try {
                    const completedItems: SyncQueueItem[] = request.result
                    for (const item of completedItems) {
                        await new Promise<void>((res, rej) => {
                            const deleteRequest = store.delete(item.id)
                            deleteRequest.onsuccess = () => res()
                            deleteRequest.onerror = () => rej(deleteRequest.error)
                        })
                    }
                    resolve()
                } catch (e) {
                    reject(e as any)
                }
            }
            request.onerror = () => reject(request.error)
        })
    }

    async retryFailedItems(): Promise<void> {
        const db = offlineDatabase.getDatabase()
        const transaction = db.transaction('syncQueue', 'readwrite')
        const store = transaction.objectStore('syncQueue')
        const index = store.index('status')
        const request = index.getAll('failed')

        await new Promise<void>((resolve, reject) => {
            request.onsuccess = async () => {
                try {
                    const failedItems: SyncQueueItem[] = request.result
                    for (const item of failedItems) {
                        item.status = 'pending'
                        item.retryCount = 0
                        item.lastError = undefined
                        item.updatedAt = new Date().toISOString()
                        await this.updateQueueItem(store, item)
                    }
                    resolve()
                } catch (e) {
                    reject(e as any)
                }
            }
            request.onerror = () => reject(request.error)
        })
    }

    async cleanupOrphanedSyncItems(): Promise<void> {
        try {
            const queueItems = await this.getAllSyncQueueItems()
            const db = offlineDatabase.getDatabase()
            const transaction = db.transaction('syncQueue', 'readwrite')
            const store = transaction.objectStore('syncQueue')

            for (const item of queueItems) {
                let entityExists = false

                switch (item.entityType) {
                    case 'patient':
                        const patient = await patientOfflineStore.get(item.entityId)
                        entityExists = !!patient
                        break
                    case 'consultation':
                        const consultation = await consultationOfflineStore.get(item.entityId)
                        entityExists = !!consultation
                        break
                    case 'document':
                        // Check both regular get and by offlineId
                        let document = await documentOfflineStore.get(item.entityId)
                        if (!document) {
                            const allDocuments = await documentOfflineStore.getAll()
                            document = allDocuments.find(doc =>
                                doc._offlineId === item.entityId ||
                                doc._id === item.entityId
                            )
                        }
                        entityExists = !!document
                        break
                    default:
                        entityExists = true // Don't remove unknown types
                }

                if (!entityExists && (item.status === 'pending' || item.status === 'failed')) {
                    console.log(`Cleaning up orphaned sync item: ${item.entityType} ${item.entityId}`)
                    await new Promise<void>((resolve, reject) => {
                        const deleteRequest = store.delete(item.id)
                        deleteRequest.onsuccess = () => resolve()
                        deleteRequest.onerror = () => reject(deleteRequest.error)
                    })
                }
            }

            console.log('Finished cleaning up orphaned sync items')
        } catch (error) {
            console.error('Failed to cleanup orphaned sync items:', error)
        }
    }

    // Alias methods for container compatibility
    async syncNow(): Promise<void> {
        // First, clean up any orphaned sync items
        await this.cleanupOrphanedSyncItems()

        // Then, scan for unqueued items and add them to queue
        await this.scanAndQueueUnsyncedItems()

        // Ensure previously failed items are re-queued for processing
        await this.retryFailedItems()

        return this.triggerManualSync()
    }

    async getQueueItems(): Promise<SyncQueueItem[]> {
        return this.getAllSyncQueueItems()
    }

    async clearAllOfflineData(): Promise<void> {
        try {
            // Stop any ongoing sync
            await this.stopPeriodicSync()

            const db = offlineDatabase.getDatabase()
            const storeNames = [
                'patients',
                'consultations',
                'documents',
                'formSubmissions',
                'transcriptions',
                'biologicals',
                'vitalParameters',
                'syncQueue',
                'conflicts'
            ]

            // Clear all stores
            for (const storeName of storeNames) {
                if (db.objectStoreNames.contains(storeName)) {
                    const transaction = db.transaction(storeName, 'readwrite')
                    const store = transaction.objectStore(storeName)
                    await new Promise((resolve, reject) => {
                        const request = store.clear()
                        request.onsuccess = () => resolve(void 0)
                        request.onerror = () => reject(request.error)
                    })
                }
            }

            // Clear localStorage related to sync
            localStorage.removeItem('last_sync_timestamp')

            // Restart periodic sync
            this.startPeriodicSync()

            // Notify listeners of cleared state
            const clearedStatus: SyncStatus = {
                isActive: false,
                pendingCount: 0,
                failedCount: 0,
                completedCount: 0,
                totalCount: 0,
                hasConflicts: false,
                lastSync: undefined
            }
            this.notifySyncStatus(clearedStatus)

        } catch (error) {
            console.error('Failed to clear offline data:', error)
            throw error
        }
    }

    // Update consultations with patient's cloud ID after patient sync
    private async updateConsultationsWithPatientCloudId(offlinePatientId: string, cloudPatientId: string): Promise<void> {
        try {
            // Get all consultations that reference this patient
            const allConsultations = await consultationOfflineStore.getAll()
            const patientConsultations = allConsultations.filter(consultation => {
                if (typeof consultation.patient === 'string') {
                    // Check if it matches the offline patient ID (before sync)
                    return consultation.patient === offlinePatientId
                } else {
                    // Check if it matches the offline patient ID in the patient object
                    const patientAsAny = consultation.patient as any
                    return consultation.patient._id === offlinePatientId ||
                        patientAsAny._offlineId === offlinePatientId ||
                        patientAsAny._localId === offlinePatientId
                }
            })

            // Update each consultation with the cloud patient ID
            for (const consultation of patientConsultations) {
                const updatedConsultation = {
                    ...consultation,
                    patient: cloudPatientId as any, // Replace with cloud ID - casting to handle type mismatch
                    _lastModified: Date.now()
                }
                await consultationOfflineStore.update(updatedConsultation)
                console.log(`Updated consultation ${consultation._id} with patient cloud ID ${cloudPatientId}`)
            }
        } catch (error) {
            console.error('Failed to update consultations with patient cloud ID:', error)
        }
    }

    // Scan for existing unsynced items and add them to queue
    async scanAndQueueUnsyncedItems(): Promise<void> {
        try {
            // Get all pending patients
            const pendingPatients = await patientOfflineStore.getBySyncStatus('pending')
            for (const patient of pendingPatients) {
                // Check if already in queue
                const queueItems = await this.getAllSyncQueueItems()
                const alreadyQueued = queueItems.some(item =>
                    item.entityType === 'patient' &&
                    item.entityId === (patient._offlineId || patient._id)
                )

                if (!alreadyQueued) {
                    await this.addToSyncQueue({
                        entityType: 'patient',
                        entityId: patient._offlineId || patient._id,
                        operation: 'create',
                        priority: 2,
                        dependencies: [],
                        maxRetries: 3,
                        payload: patient,
                        data: patient,
                        timestamp: patient._createdAt || Date.now()
                    })
                }
            }

            // Get all pending consultations
            const pendingConsultations = await consultationOfflineStore.getBySyncStatus('pending')
            for (const consultation of pendingConsultations) {
                // Check if already in queue
                const queueItems = await this.getAllSyncQueueItems()
                const alreadyQueued = queueItems.some(item =>
                    item.entityType === 'consultation' &&
                    item.entityId === (consultation._offlineId || consultation._id)
                )

                if (!alreadyQueued) {
                    // Determine dependencies
                    const dependencies: string[] = []
                    if (consultation._patientSyncRequired && typeof consultation.patient !== 'string') {
                        const patientAsAny = consultation.patient as any
                        const patientId = patientAsAny._offlineId || consultation.patient._id
                        // Try to find an existing patient queue item by entityId
                        let currentQueue = await this.getAllSyncQueueItems()
                        let patientQueueItem = currentQueue.find(item => item.entityType === 'patient' && item.entityId === patientId)
                        if (!patientQueueItem) {
                            // Ensure patient is queued first
                            await this.addToSyncQueue({
                                entityType: 'patient',
                                entityId: patientId,
                                operation: 'create',
                                priority: 2,
                                dependencies: [],
                                maxRetries: 3,
                                payload: consultation.patient,
                                data: consultation.patient,
                                timestamp: (consultation.patient as any)._createdAt || Date.now()
                            })
                            currentQueue = await this.getAllSyncQueueItems()
                            patientQueueItem = currentQueue.find(item => item.entityType === 'patient' && item.entityId === patientId)
                        }
                        if (patientQueueItem) {
                            dependencies.push(patientQueueItem.id)
                        }
                    }

                    await this.addToSyncQueue({
                        entityType: 'consultation',
                        entityId: consultation._offlineId || consultation._id,
                        operation: 'create',
                        priority: 1,
                        dependencies,
                        maxRetries: 3,
                        payload: consultation,
                        data: consultation,
                        timestamp: consultation._createdAt || Date.now()
                    })
                }
            }

            // Get all pending documents
            const pendingDocuments = await documentOfflineStore.getBySyncStatus('pending')
            for (const document of pendingDocuments) {
                // Check if already in queue
                let queueItems = await this.getAllSyncQueueItems()
                const alreadyQueued = queueItems.some(item =>
                    item.entityType === 'document' &&
                    item.entityId === (document._offlineId || document._id)
                )

                if (!alreadyQueued) {
                    // Determine dependencies - documents should sync after their parent objects
                    const dependencies: string[] = []

                    // If the document belongs to a consultation or patient that needs syncing
                    if (document.objectType === 'consultation') {
                        const consultation = await consultationOfflineStore.get(document.objectId)
                        if (consultation && consultation._syncStatus !== 'synced') {
                            // Find the consultation's sync queue item ID
                            let currentQueue = await this.getAllSyncQueueItems()
                            let consultationQueueItem = currentQueue.find(item =>
                                item.entityType === 'consultation' &&
                                item.entityId === (consultation._offlineId || consultation._id)
                            )
                            if (!consultationQueueItem) {
                                // If consultation isn't in queue yet, add it first
                                await this.addToSyncQueue({
                                    entityType: 'consultation',
                                    entityId: consultation._offlineId || consultation._id,
                                    operation: 'create',
                                    priority: 1,
                                    dependencies: [],
                                    maxRetries: 3,
                                    payload: consultation,
                                    data: consultation,
                                    timestamp: consultation._createdAt || Date.now()
                                })
                                currentQueue = await this.getAllSyncQueueItems()
                                consultationQueueItem = currentQueue.find(item =>
                                    item.entityType === 'consultation' &&
                                    item.entityId === (consultation._offlineId || consultation._id)
                                )
                            }
                            if (consultationQueueItem) {
                                dependencies.push(consultationQueueItem.id)
                            }
                        }
                    } else if (document.objectType === 'patient') {
                        const patient = await patientOfflineStore.get(document.objectId)
                        if (patient && patient._syncStatus !== 'synced') {
                            // Find the patient's sync queue item ID
                            let currentQueue = await this.getAllSyncQueueItems()
                            let patientQueueItem = currentQueue.find(item =>
                                item.entityType === 'patient' &&
                                item.entityId === (patient._offlineId || patient._id)
                            )
                            if (!patientQueueItem) {
                                // If patient isn't in queue yet, add it first
                                await this.addToSyncQueue({
                                    entityType: 'patient',
                                    entityId: patient._offlineId || patient._id,
                                    operation: 'create',
                                    priority: 2,
                                    dependencies: [],
                                    maxRetries: 3,
                                    payload: patient,
                                    data: patient,
                                    timestamp: patient._createdAt || Date.now()
                                })
                                currentQueue = await this.getAllSyncQueueItems()
                                patientQueueItem = currentQueue.find(item =>
                                    item.entityType === 'patient' &&
                                    item.entityId === (patient._offlineId || patient._id)
                                )
                            }
                            if (patientQueueItem) {
                                dependencies.push(patientQueueItem.id)
                            }
                        }
                    }

                    await this.addToSyncQueue({
                        entityType: 'document',
                        entityId: document._offlineId || document._id,
                        operation: 'create',
                        priority: 0, // Lower priority than patients and consultations
                        dependencies,
                        maxRetries: 3,
                        payload: document,
                        data: document,
                        timestamp: document._createdAt || Date.now()
                    })
                } else {
                    // If it's already queued, ensure dependencies are set correctly
                    queueItems = await this.getAllSyncQueueItems()
                    const docQueueItem = queueItems.find(item =>
                        item.entityType === 'document' &&
                        item.entityId === (document._offlineId || document._id)
                    )
                    if (docQueueItem) {
                        const dependencies: string[] = []
                        if (document.objectType === 'consultation') {
                            const consultation = await consultationOfflineStore.get(document.objectId)
                            if (consultation && consultation._syncStatus !== 'synced') {
                                let currentQueue = await this.getAllSyncQueueItems()
                                let consultationQueueItem = currentQueue.find(item =>
                                    item.entityType === 'consultation' &&
                                    item.entityId === (consultation._offlineId || consultation._id)
                                )
                                if (!consultationQueueItem) {
                                    await this.addToSyncQueue({
                                        entityType: 'consultation',
                                        entityId: consultation._offlineId || consultation._id,
                                        operation: 'create',
                                        priority: 1,
                                        dependencies: [],
                                        maxRetries: 3,
                                        payload: consultation,
                                        data: consultation,
                                        timestamp: consultation._createdAt || Date.now()
                                    })
                                    currentQueue = await this.getAllSyncQueueItems()
                                    consultationQueueItem = currentQueue.find(item =>
                                        item.entityType === 'consultation' &&
                                        item.entityId === (consultation._offlineId || consultation._id)
                                    )
                                }
                                if (consultationQueueItem) {
                                    // Ensure the consultation queue item is pending
                                    if (consultationQueueItem.status !== 'pending') {
                                        const db = offlineDatabase.getDatabase()
                                        const transaction = db.transaction('syncQueue', 'readwrite')
                                        const store = transaction.objectStore('syncQueue')
                                        consultationQueueItem.status = 'pending'
                                        consultationQueueItem.retryCount = 0
                                        consultationQueueItem.updatedAt = new Date().toISOString()
                                        await this.updateQueueItem(store, consultationQueueItem)
                                    }
                                    dependencies.push(consultationQueueItem.id)
                                }
                            }
                        } else if (document.objectType === 'patient') {
                            const patient = await patientOfflineStore.get(document.objectId)
                            if (patient && patient._syncStatus !== 'synced') {
                                let currentQueue = await this.getAllSyncQueueItems()
                                let patientQueueItem = currentQueue.find(item =>
                                    item.entityType === 'patient' &&
                                    item.entityId === (patient._offlineId || patient._id)
                                )
                                if (!patientQueueItem) {
                                    await this.addToSyncQueue({
                                        entityType: 'patient',
                                        entityId: patient._offlineId || patient._id,
                                        operation: 'create',
                                        priority: 2,
                                        dependencies: [],
                                        maxRetries: 3,
                                        payload: patient,
                                        data: patient,
                                        timestamp: patient._createdAt || Date.now()
                                    })
                                    currentQueue = await this.getAllSyncQueueItems()
                                    patientQueueItem = currentQueue.find(item =>
                                        item.entityType === 'patient' &&
                                        item.entityId === (patient._offlineId || patient._id)
                                    )
                                }
                                if (patientQueueItem) {
                                    if (patientQueueItem.status !== 'pending') {
                                        const db = offlineDatabase.getDatabase()
                                        const transaction = db.transaction('syncQueue', 'readwrite')
                                        const store = transaction.objectStore('syncQueue')
                                        patientQueueItem.status = 'pending'
                                        patientQueueItem.retryCount = 0
                                        patientQueueItem.updatedAt = new Date().toISOString()
                                        await this.updateQueueItem(store, patientQueueItem)
                                    }
                                    dependencies.push(patientQueueItem.id)
                                }
                            }
                        }

                        // Update the document queue item if dependencies changed
                        const depsChanged = JSON.stringify(docQueueItem.dependencies || []) !== JSON.stringify(dependencies)
                        if (depsChanged) {
                            const db = offlineDatabase.getDatabase()
                            const transaction = db.transaction('syncQueue', 'readwrite')
                            const store = transaction.objectStore('syncQueue')
                            docQueueItem.dependencies = dependencies
                            docQueueItem.updatedAt = new Date().toISOString()
                            // Ensure the document is pending for re-processing
                            if (docQueueItem.status !== 'pending') {
                                docQueueItem.status = 'pending'
                                docQueueItem.retryCount = 0
                            }
                            await this.updateQueueItem(store, docQueueItem)
                        }
                    }
                }
            }

            console.log('Finished scanning and queuing unsynced items')
        } catch (error) {
            console.error('Failed to scan and queue unsynced items:', error)
        }
    }

    private async updateLocalConsultationAfterDocumentSync(consultationId: string, updatedConsultation: Consultation): Promise<void> {
        try {
            // Get the local consultation from the store using the cloud ID
            let localConsultation = await consultationOfflineStore.get(consultationId)

            // If not found by cloud ID, try to find by _cloudId field
            if (!localConsultation) {
                const allConsultations = await consultationOfflineStore.getAll()
                localConsultation = allConsultations.find(c => c._cloudId === consultationId)
            }

            if (!localConsultation) {
                console.warn(`Local consultation not found for cloud ID: ${consultationId}`)
                return
            }

            // Merge the updated data from backend with the local consultation
            const updatedLocalConsultation = {
                ...localConsultation,
                // Update fields that might have changed on the backend
                biologicals: updatedConsultation.biologicals || localConsultation.biologicals,
                files: updatedConsultation.files || localConsultation.files,
                mesures: updatedConsultation.mesures || localConsultation.mesures,
                _lastModified: Date.now(),
                _syncStatus: 'synced' as const
            }

            await consultationOfflineStore.update(updatedLocalConsultation)
            console.log(`Updated local consultation ${localConsultation._id} with updated data from backend`)
        } catch (error) {
            console.error('Failed to update consultation after document sync:', error)
            // Don't throw here as the document sync was successful, this is just a local update issue
        }
    }

    // Add progress event listeners
    onSyncProgress(callback: (progress: SyncProgress) => void) {
        this.progressListeners.push(callback)
    }

    // Add authentication error listeners
    onAuthError(callback: (retryCallback: () => Promise<void>) => void): () => void {
        this.authErrorListeners.push(callback)
        return () => {
            this.authErrorListeners = this.authErrorListeners.filter(l => l !== callback)
        }
    }

    private notifyAuthError(retryCallback: () => Promise<void>) {
        this.authErrorListeners.forEach(listener => {
            try {
                listener(retryCallback)
            } catch (error) {
                console.error('Error in auth error listener:', error)
            }
        })
    }

    // Wrapper method for API calls with authentication error handling
    private async executeWithAuthHandling<T>(
        apiCall: () => Promise<T>,
        context: string,
        retryCallback?: () => Promise<void>
    ): Promise<T> {
        try {
            return await apiCall()
        } catch (error) {
            if (error instanceof Error && error.message.includes('Authentication expired during sync operation')) {
                console.log(`Authentication error during ${context}, triggering re-auth`)

                // Return a promise that resolves when re-auth is complete and retry is called
                return new Promise<T>((resolve, reject) => {
                    const retryWithResolve = async () => {
                        try {
                            console.log(`Retrying ${context} after re-authentication`)
                            const result = await apiCall()
                            resolve(result)
                        } catch (retryError) {
                            console.error(`Retry failed for ${context}:`, retryError)
                            reject(retryError)
                        }
                    }

                    // Notify listeners about the auth error
                    this.notifyAuthError(retryWithResolve)
                })
            } else {
                // Re-throw non-auth errors
                throw error
            }
        }
    }

    private notifyProgress(progress: SyncProgress) {
        this.progressListeners.forEach(callback => callback(progress))
    }

    // Move any stuck 'processing' items back to 'pending' so they can be retried
    private async recoverProcessingItems(): Promise<void> {
        try {
            const db = offlineDatabase.getDatabase()
            const transaction = db.transaction('syncQueue', 'readwrite')
            const store = transaction.objectStore('syncQueue')
            const index = store.index('status')
            const request = index.getAll('processing')

            await new Promise<void>((resolve) => {
                request.onsuccess = async () => {
                    const stuckItems: SyncQueueItem[] = request.result
                    for (const item of stuckItems) {
                        item.status = 'pending'
                        item.updatedAt = new Date().toISOString()
                        item.lastError = item.lastError || 'Recovered from processing state'
                        await this.updateQueueItem(store, item)
                    }
                    resolve()
                }
                request.onerror = () => resolve()
            })
        } catch (error) {
            console.warn('Failed to recover processing items:', error)
        }
    }

    private getItemDescription(item: SyncQueueItem): string {
        const action = item.action || item.operation
        const entityType = item.entityType

        switch (entityType) {
            case 'patient':
                return `${action} patient`
            case 'consultation':
                return `${action} consultation`
            case 'document':
                return `${action} document`
            default:
                return `${action} ${entityType}`
        }
    }
}

// Create a singleton instance
export const syncService = new SyncService() 
